/**
 * PillTabs — a row of pill-shaped tabs over one panel. The brand reference's
 * category switch (All / Events / Spaces / Equipment), 1 October 2026.
 *
 * A real ARIA tablist rather than a row of toggle buttons: one tab stop for the
 * whole row (roving `tabIndex`), arrow keys to move between tabs, Home/End to
 * jump, and each tab pointing at the panel it controls. Activation is
 * automatic — focusing a tab selects it — because switching costs nothing here:
 * every panel's data is already in memory.
 *
 * ⚠️ **The arrow keys follow reading direction.** In Arabic the row runs right
 * to left, so ArrowLeft moves to the *next* tab. Reading `<html dir>` through
 * `useLocale` (as `Select` does) rather than hard-wiring ArrowRight = next is
 * what keeps the keyboard and the eye moving the same way.
 *
 * ⚠️ **The selected pill is a wine fill with a `brand-fg` edge, not a
 * `brand-600` one.** In dark mode the 600 fill measures ~2.5:1 against the
 * dark ground — under the 3:1 a state indicator owes (WCAG 1.4.11) — so the
 * light-wine edge is what carries "selected" there. `aria-selected` carries it
 * to assistive tech, and the fill-versus-outline shape is the non-colour
 * signal (rule 4).
 *
 * Rule 1 holds: the tab labels and the row's accessible name arrive as props.
 */
import { useRef } from 'react'

import { useLocale } from '@/shared/i18n/useLocale'

import { cn } from './cn'
import { pillTabId } from './pillTabId'

export type PillTab<Value extends string> = { value: Value; label: string }

export type PillTabsProps<Value extends string> = {
  tabs: readonly PillTab<Value>[]
  value: Value
  onValueChange: (value: Value) => void
  /** Accessible name for the tablist. */
  label: string
  /** The id of the panel every tab controls — the caller renders it with
   *  `role="tabpanel"` and `aria-labelledby={tabId(value)}`. */
  panelId: string
  /** Prefix for each tab's id, so the panel can name the active tab. */
  idPrefix: string
  /**
   * Below `sm`, the tabs share the full row equally and tighten (13px, 8px
   * sides) instead of wrapping — the reference's phone filter row
   * (`.tabs button { flex: 1 }`). From `sm` up they are their natural width.
   */
  stretch?: boolean | undefined
  className?: string | undefined
}

export function PillTabs<Value extends string>({
  tabs,
  value,
  onValueChange,
  label,
  panelId,
  idPrefix,
  stretch = false,
  className,
}: PillTabsProps<Value>) {
  const { direction } = useLocale()
  const refs = useRef(new Map<Value, HTMLButtonElement>())

  function move(to: number) {
    const tab = tabs[(to + tabs.length) % tabs.length]
    if (!tab) return
    onValueChange(tab.value)
    refs.current.get(tab.value)?.focus()
  }

  function onKeyDown(event: React.KeyboardEvent, index: number) {
    const forward = direction === 'rtl' ? 'ArrowLeft' : 'ArrowRight'
    const backward = direction === 'rtl' ? 'ArrowRight' : 'ArrowLeft'
    if (event.key === forward) move(index + 1)
    else if (event.key === backward) move(index - 1)
    else if (event.key === 'Home') move(0)
    else if (event.key === 'End') move(tabs.length - 1)
    else return
    event.preventDefault()
  }

  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn(
        'flex gap-2',
        stretch ? 'w-full gap-1.5 sm:w-auto sm:flex-wrap sm:gap-2.5' : 'flex-wrap',
        className,
      )}
    >
      {tabs.map((tab, index) => {
        const selected = tab.value === value
        return (
          <button
            key={tab.value}
            ref={(node) => {
              if (node) refs.current.set(tab.value, node)
              else refs.current.delete(tab.value)
            }}
            type="button"
            role="tab"
            id={pillTabId(idPrefix, tab.value)}
            aria-selected={selected}
            aria-controls={panelId}
            tabIndex={selected ? 0 : -1}
            onClick={() => onValueChange(tab.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={cn(
              'min-h-9 rounded-full border px-5 py-1.5 text-sm transition-colors',
              stretch && 'flex-1 px-2 text-[13px] sm:flex-none sm:px-[21px] sm:text-sm',
              selected
                ? 'border-brand-fg bg-brand-600 font-semibold text-fg-onbrand'
                : 'border-line bg-surface text-fg-muted hover:bg-hover hover:text-fg',
            )}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}
